import firebase from "firebase/compat/app";
import { firestore } from "../firebaseConfig";
import {
  Timestamp,
  IAddress,
  ICompany,
  ICompanyPaymentTerms,
  ICompanyAccountingSettings,
} from "../types";
import { query, doc } from "firebase/firestore";
import { getApiToken } from "../data/api/token";
import {
  createCompany as createCompanyApi,
  updateCompany as updateCompanyApi,
  deleteCompany as deleteCompanyApi,
} from "../data/api/companies";

type DocumentSnapshot = firebase.firestore.DocumentSnapshot;
type SnapshotOptions = firebase.firestore.SnapshotOptions;

class Company implements ICompany {
  id: string;
  workspaceId: string;
  name: string;
  logoUrl?: string | null;
  logoStoragePath?: string | null;
  notificationSettings?: ICompany["notificationSettings"];
  contactEmail?: string | null;
  invoiceEmail?: string | null;
  primaryAddress: IAddress | null;
  createdAt?: Timestamp;
  organizationNumber: string;
  paymentTerms: ICompanyPaymentTerms;
  accountingSettings?: ICompanyAccountingSettings;
  static collectionName = "companies";

  static converter = {
    toFirestore(company: Company) {
      return company.data();
    },
    fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions) {
      const data = snapshot.data(options) as ICompany;
      return new Company({ ...data, id: snapshot.id });
    },
  };

  static createId() {
    return firestore.collection(Company.collectionName).doc().id;
  }

  constructor({
    id,
    workspaceId,
    name,
    logoUrl,
    logoStoragePath,
    notificationSettings,
    contactEmail,
    invoiceEmail,
    primaryAddress,
    createdAt,
    organizationNumber,
    paymentTerms,
    accountingSettings,
  }: ICompany) {
    this.id = id;
    this.workspaceId = workspaceId;
    this.name = name ?? "";
    this.logoUrl = logoUrl || null;
    this.logoStoragePath = logoStoragePath || null;
    this.notificationSettings = notificationSettings || null;
    this.organizationNumber = organizationNumber;
    this.contactEmail = contactEmail || null;
    this.invoiceEmail = invoiceEmail || null;
    this.primaryAddress = primaryAddress || null;
    this.createdAt = createdAt;
    this.paymentTerms = paymentTerms;
    this.accountingSettings = accountingSettings;
  }

  clone() {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this);
  }

  setData(updates: Partial<ICompany>) {
    return Object.assign(this, updates);
  }

  data() {
    return {
      id: this.id || "",
      workspaceId: this.workspaceId,
      name: this.name,
      logoUrl: this.logoUrl || null,
      logoStoragePath: this.logoStoragePath || null,
      notificationSettings: this.notificationSettings || null,
      organizationNumber: this.organizationNumber,
      invoiceEmail: this.invoiceEmail || null,
      contactEmail: this.contactEmail || null,
      primaryAddress: this.primaryAddress || null,
      createdAt: this.createdAt || null,
      paymentTerms: this.paymentTerms || null,
      accountingSettings: this.accountingSettings || null,
    };
  }

  static default = (): Company => {
    return new Company({
      id: "",
      workspaceId: "",
      name: "",
      logoUrl: null,
      logoStoragePath: null,
      organizationNumber: "",
      createdAt: undefined,
      paymentTerms: {
        accountNumber: "",
        iban: "",
        swift: "",
        paymentDays: 30,
        isVatRegistered: false,
      },
      accountingSettings: {
        accountingEmail: "",
        forwardInvoice: false,
      },
    });
  };

  static isNew(data: ICompany): boolean {
    return data.id.length === 0;
  }

  static get = (id: string) => {
    return doc(
      firestore
        .collection(Company.collectionName)
        .withConverter(Company.converter),
      id,
    );
  };

  static list = (workspaceId: string) => {
    return query(
      firestore
        .collection(Company.collectionName)
        .where("workspaceId", "==", workspaceId)
        .withConverter(Company.converter),
    );
  };

  static create = async (
    workspaceId: string,
    data: Omit<ICompany, "id" | "createdAt">,
  ) => {
    const newCompany = new Company({
      ...data,
      workspaceId: workspaceId,
      id: "",
    });

    const token = await getApiToken();
    const created = await createCompanyApi(
      token,
      workspaceId,
      newCompany.data(),
    );

    return new Company(created);
  };

  static update = async (company: Company, updates: Partial<ICompany>) => {
    const updateType =
      (company.clone && company.clone()) || new Company({ ...company });

    updateType.setData({
      ...updates,
    });

    const token = await getApiToken();
    const updated = await updateCompanyApi(
      token,
      updateType.workspaceId,
      company.id,
      updateType.data(),
    );

    return new Company(updated);
  };

  static remove = async (company: Company) => {
    const token = await getApiToken();
    return await deleteCompanyApi(token, company.workspaceId, company.id);
  };
}

export default Company;
